import {
  Confirm,
  Dispute,
  DisputeToShopee,
  ConfirmShopeeDecision,
  // Detail,
  ProposeConsult,
  ProposeAccept,
  SubmitEvidence,
  WaitReturn,
  DisputeRetract,
  ModifyPrice,
  ConfirmReturnRefund,
  PreDisputeToShopee,
} from 'components/Refund';
import { CommonRefund, returnReviewResolve } from 'services/api/return';
import { ButtonProps } from 'antd/es/button';
import { Refuse } from './Refuse';
import ForwardToAgent from './ForwardToAgent';
import TikTokRefuse from './TikTokRefuse';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';
import { getReturnActionMercado } from 'views/Refund/components/ReturnActions/ReturnActionMercado';
import { get } from 'lodash-es';
import { ReturnOrderListItemV2 } from 'services/api/refund';
import { TikTokConfirmChange } from './TikTokConfirmChange';
import { FakeRefuseButton } from './FakeRefuse';
import UndoDispute from './UndoDispute';
import StatusButton from 'components/StatusButton';
import { useTranslate } from '@tolgee/react';
import { message } from 'antd';

interface IOperateBtns extends CommonRefund {
  platform?: General.PlatformUpperCase;
  operate: Conversation.Trade.IReturnOperationType[];
  refreshList?: () => void;
  internalStoreId: string;
  internalTradeId: string;
  returnOrderItemId?: string[];
  unit: string;
  refundAmount: number;
  latestOfferAmount: number;
  allowOffer: Conversation.Trade.IAllowOffer[];
  displayStatus: Conversation.Trade.IDisplayStatus;
  offerCounterLimit: number;
  operationLog: Conversation.Trade.IReturnOperationLog[];
  uiContactBuyerBtn?: JSX.Element;
  size?: ButtonProps['size'];
  preDispute?: ReturnOrderListItemV2['preDispute'];
  region?: General.UpperCaseRegion;
  type?: ButtonProps['type'];
}

/** 退款操作按钮: IReturnOperationType
 * ```
 * https://leyan.yuque.com/czpqnb/pd/qgegdo#PxjOF
 * 下面的按钮有顺序先后.
 * ```
 */
const operateBtns = (props: IOperateBtns) => {
  const {
    platform,
    operate,
    refreshList,
    internalStoreId,
    externalStoreId,
    internalTradeId,
    externalTradeId,
    returnOrderItemId,
    returnSn,
    unit,
    refundAmount,
    latestOfferAmount,
    allowOffer,
    operationLog,
    offerCounterLimit,
    uiContactBuyerBtn,
    displayStatus,
    preDispute,
    region,
    ...btnProps
  } = props;
  const optBtns = [];

  const refuseOperate = operate.find(
    (item) => item === 'REFUSE' || item === 'REFUSE_CHANGE' || item === 'RETURN_REVIEW_FAIL',
  );
  const agreeOnlyRefundOperate = operate.find(
    (item) => item === 'FULL_REFUND' || item === 'CONFIRM_ONLY_REFUND',
  );

  // 上传证据(不支持)
  optBtns.push(
    operate?.includes('SUBMIT_EVIDENCE') ? (
      <SubmitEvidence
        platform={platform}
        key="SUBMIT_EVIDENCE"
        internalStoreId={internalStoreId}
        externalStoreId={externalStoreId}
        externalTradeId={externalTradeId}
        returnSn={returnSn}
        {...btnProps}
      />
    ) : null,
  );

  // 全额退款
  optBtns.push(
    agreeOnlyRefundOperate ? (
      <Confirm
        key="agreeOnlyRefundOperate"
        externalStoreId={externalStoreId}
        externalTradeId={externalTradeId}
        returnSn={returnSn}
        onSuccess={refreshList}
        operate={agreeOnlyRefundOperate}
        unit={unit}
        platform={platform}
        refundAmount={refundAmount}
        {...btnProps}
      />
    ) : null,
  );

  // 接受提案: 接受买家提案
  optBtns.push(
    operate?.includes('ACCEPT_OFFER') ? (
      <ProposeAccept
        key="ACCEPT_OFFER"
        returnSn={returnSn}
        unit={unit}
        latestOfferAmount={latestOfferAmount}
        onSuccess={refreshList}
        {...btnProps}
      />
    ) : null,
  );

  // 接受仅退款申请
  optBtns.push(
    operate?.includes('CONFIRM') ? (
      <Confirm
        key="CONFIRM"
        externalStoreId={externalStoreId}
        externalTradeId={externalTradeId}
        returnSn={returnSn}
        onSuccess={refreshList}
        operate="CONFIRM"
        platform={platform}
        returnOrderItemId={returnOrderItemId}
        unit={unit}
        refundAmount={refundAmount}
        {...btnProps}
      />
    ) : null,
  );

  // 接受退货退款申请
  if (platform === 'MERCADO') {
    optBtns.push(
      ...getReturnActionMercado({
        unit,
        refundAmount,
        externalStoreId,
        externalTradeId,
        returnSn,
        operate,
        platform,
        refreshList,
        internalStoreId,
      }),
    );
  } else {
    optBtns.push(
      operate?.includes('CONFIRM_RETURN_REFUND') ? (
        <ConfirmReturnRefund
          key="CONFIRM_RETURN_REFUND"
          externalStoreId={externalStoreId}
          externalTradeId={externalTradeId}
          returnSn={returnSn}
          platform={platform}
          returnOrderItemId={returnOrderItemId}
          onSucceed={refreshList}
          {...btnProps}
        />
      ) : null,
    );
  }

  if (operate.includes('CONFIRM_CHANGE')) {
    optBtns.push(
      <TikTokConfirmChange
        externalStoreId={externalStoreId}
        orderSn={externalTradeId}
        returnSn={returnSn}
        platform={platform}
        refreshList={refreshList}
        btnProps={btnProps}
      />,
    );
  }

  if (refuseOperate) {
    if (isTiKTokUpperCase(platform)) {
      optBtns.push(
        <TikTokRefuse
          platform={platform}
          region={region}
          internalStoreId={internalStoreId}
          externalTradeId={externalTradeId}
          returnSn={returnSn}
          externalStoreId={externalStoreId}
          onRefused={refreshList}
          operate={refuseOperate}
          btnProps={btnProps}
        />,
      );
    } else {
      if (operate.includes('REFUSE') && returnOrderItemId) {
        optBtns.push(
          <Refuse
            key="REFUSE"
            internalStoreId={internalStoreId}
            externalStoreId={externalStoreId}
            returnSn={returnSn}
            returnOrderItemId={returnOrderItemId}
            onRefused={refreshList}
          />,
        );
      }
    }
  }

  if (operate?.includes('FAKE_REFUSE')) {
    optBtns.push(<FakeRefuseButton />);
  }

  if (operate.includes('FORWARD_TO_AGENT')) {
    optBtns.push(<ForwardToAgent />);
  }

  // 争议
  optBtns.push(
    operate?.includes('DISPUTE') && platform !== 'MERCADO' ? (
      <Dispute
        key="DISPUTE"
        internalStoreId={internalStoreId}
        externalStoreId={externalStoreId}
        internalTradeId={internalTradeId}
        externalTradeId={externalTradeId}
        returnSn={returnSn}
        onSuccess={refreshList}
        {...btnProps}
      />
    ) : null,
  );

  // 提出部分退款, 再次提案
  optBtns.push(
    operate?.includes('OFFER') && allowOffer?.some((v) => v.returnType === 'ONLY_REFUND') ? (
      <ProposeConsult
        key="OFFER"
        externalStoreId={externalStoreId}
        externalTradeId={externalTradeId}
        returnSn={returnSn}
        unit={unit}
        allowOffer={allowOffer}
        displayStatus={displayStatus}
        operationLog={operationLog}
        offerCounterLimit={offerCounterLimit}
        onSuccess={refreshList}
        {...btnProps}
      >
        {uiContactBuyerBtn}
      </ProposeConsult>
    ) : null,
  );

  // 等待退货(不支持)
  optBtns.push(
    operate?.includes('WAIT_RETURN') ? (
      <WaitReturn
        onSucceed={refreshList}
        returnSn={returnSn}
        platform={platform}
        key="WAIT_RETURN"
        {...btnProps}
      />
    ) : null,
  );

  // 接受Shopee决定(不支持)
  optBtns.push(
    operate?.includes('CONFIRM_SHOPEE_DECISION') ? (
      <ConfirmShopeeDecision key="CONFIRM_SHOPEE_DECISION" {...btnProps} />
    ) : null,
  );

  optBtns.push(
    operate?.includes('DISPUTE_TO_SHOPEE') ? (
      <DisputeToShopee
        key="DISPUTE_TO_SHOPEE"
        internalStoreId={internalStoreId}
        externalStoreId={externalStoreId}
        internalTradeId={internalTradeId}
        externalTradeId={externalTradeId}
        returnSn={returnSn}
        onSuccess={refreshList}
        preDisputeContent={get(preDispute, 'content', '')}
        failReasonCode={get(preDispute, 'failReasonCode', 0)}
        region={region}
        {...btnProps}
      />
    ) : null,
  );

  optBtns.push(
    operate?.includes('PRE_DISPUTE') || operate?.includes('UPDATE_PRE_DISPUTE') ? (
      <PreDisputeToShopee
        key="PRE_DISPUTE_TO_SHOPEE"
        internalStoreId={internalStoreId}
        externalStoreId={externalStoreId}
        internalTradeId={internalTradeId}
        externalTradeId={externalTradeId}
        returnSn={returnSn}
        onSuccess={refreshList}
        preDisputeContent={get(preDispute, 'content', '')}
        region={region}
        {...btnProps}
      />
    ) : null,
  );

  // 撤回争议(不支持)
  optBtns.push(
    operate?.includes('RETRACT_DISPUTE') ? (
      <DisputeRetract key="RETRACT_DISPUTE" {...btnProps} />
    ) : null,
  );

  // 修改出价(不支持)
  optBtns.push(
    operate?.includes('MODIFY_PRICE') ? (
      <ModifyPrice
        key="MODIFY_PRICE"
        externalStoreId={externalStoreId}
        externalTradeId={externalTradeId}
        returnSn={returnSn}
        unit={unit}
        allowOffer={allowOffer}
        displayStatus={displayStatus}
        operationLog={operationLog}
        offerCounterLimit={offerCounterLimit}
        onSuccess={refreshList}
        {...btnProps}
      >
        {uiContactBuyerBtn}
      </ModifyPrice>
    ) : null,
  );

  optBtns.push(operate?.includes('UNDO_DISPUTE') ? <UndoDispute /> : null);

  if (isTiKTokUpperCase(platform) && operate?.includes('RETURN_REVIEW_OK')) {
    optBtns.push(
      <ReturnReviewOk
        platform={platform}
        returnSn={returnSn}
        refreshList={refreshList}
        {...btnProps}
      />,
    );
  }

  return optBtns.filter((_) => _);
};

function ReturnReviewOk({
  platform,
  returnSn,
  refreshList,
  ...otherProps
}: ButtonProps & {
  platform: General.PlatformUpperCase;
  returnSn: string;
  refreshList?: () => void;
}) {
  const { t } = useTranslate();

  return (
    <StatusButton
      size="small"
      type="link"
      {...otherProps}
      onClick={() =>
        returnReviewResolve(platform, returnSn)
          .then(() => {
            refreshList?.();
            message.success(t('操作成功'));
          })
          .catch(() => {
            message.error(t('translation.操作失败，请稍后再试'));
          })
      }
    >
      {t('确认收到包裹')}
    </StatusButton>
  );
}

export default operateBtns;
